import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

// ── Grid layout: 6 plates in a horizontal row
const GRID_PLATE_POSITIONS = (svgW, svgH, r) => {
    const count = 6;
    const spacing = r * 2.8;
    const totalW = (count - 1) * spacing;
    const startX = svgW / 2 - totalW / 2;
    return Array.from({ length: count }, (_, i) => ({ x: startX + i * spacing, y: svgH / 2 }));
};

// ── W-pattern layout (mirroring gameplay percentages)
const W_PLATE_POSITIONS = (svgW, svgH) => [
    { x: svgW * 0.15, y: svgH * 0.25 },
    { x: svgW * 0.29, y: svgH * 0.75 },
    { x: svgW * 0.43, y: svgH * 0.45 },
    { x: svgW * 0.57, y: svgH * 0.45 },
    { x: svgW * 0.71, y: svgH * 0.75 },
    { x: svgW * 0.85, y: svgH * 0.25 },
];

// ── Texas Star: 5 plates in a circle
const STAR_PLATE_POSITIONS = (svgW, svgH, r) =>
    Array.from({ length: 5 }, (_, i) => {
        const phi = (i * 2 * Math.PI) / 5 - Math.PI / 2;
        const armR = r * 3.5;
        return { x: svgW / 2 + Math.cos(phi) * armR, y: svgH / 2 + Math.sin(phi) * armR };
    });

// ─────────────────────────────────────────────────────────────────────────────

const PlateHeatMap = ({ clicks, plateMode }) => {
    const plateR = 36;
    const padX = 60;
    const padY = 60;

    const svgW = 700;
    const svgH = plateMode === 'w_pattern' ? 380 : 260;

    let positions;
    if (plateMode === 'grid') {
        positions = GRID_PLATE_POSITIONS(svgW, svgH, plateR);
    } else if (plateMode === 'w_pattern') {
        positions = W_PLATE_POSITIONS(svgW, svgH);
    } else if (plateMode === 'texas-star') {
        positions = STAR_PLATE_POSITIONS(svgW, svgH, plateR);
    } else {
        // random — no fixed positions; show a neutral message
        return (
            <div style={{ color: '#888', fontSize: '13px', textAlign: 'center', padding: '20px' }}>
                Heat map not available for Random layout — plate positions vary each run.
            </div>
        );
    }

    return (
        <svg
            width="100%"
            height="auto"
            style={{ maxWidth: '700px', background: '#111', borderRadius: '8px', border: '1px solid #333' }}
            viewBox={`${-padX} ${-padY} ${svgW + padX * 2} ${svgH + padY * 2}`}
        >
            <defs>
                <radialGradient id="pHitGrad">
                    <stop offset="0%" stopColor="rgba(46, 204, 113, 0.7)" />
                    <stop offset="60%" stopColor="rgba(46, 204, 113, 0.2)" />
                    <stop offset="100%" stopColor="rgba(46, 204, 113, 0)" />
                </radialGradient>
                <radialGradient id="pMissGrad">
                    <stop offset="0%" stopColor="rgba(231, 76, 60, 0.7)" />
                    <stop offset="60%" stopColor="rgba(231, 76, 60, 0.2)" />
                    <stop offset="100%" stopColor="rgba(231, 76, 60, 0)" />
                </radialGradient>
            </defs>

            {/* Draw plate outlines */}
            {positions.map((pos, i) => (
                <g key={i}>
                    <circle cx={pos.x} cy={pos.y} r={plateR} fill="#2a2a2a" stroke="#555" strokeWidth="2" />
                    <circle cx={pos.x} cy={pos.y} r={plateR * 0.6} fill="none" stroke="#444" strokeWidth="1" strokeDasharray="4 3" />
                    <text x={pos.x} y={pos.y + 4} textAnchor="middle" fontSize="13" fill="#666">{i + 1}</text>
                </g>
            ))}

            {/* Plot clicks relative to their plate */}
            {clicks.map((c, i) => {
                const idx = c.plateIndex ?? 0;
                const pos = positions[idx];
                if (!pos) return null;
                const cx = pos.x + c.x * plateR;
                const cy = pos.y + c.y * plateR;
                return (
                    <circle
                        key={i}
                        cx={cx}
                        cy={cy}
                        r={14}
                        fill={c.hit ? 'url(#pHitGrad)' : 'url(#pMissGrad)'}
                        style={{ mixBlendMode: 'screen' }}
                    />
                );
            })}
        </svg>
    );
};

PlateHeatMap.propTypes = {
    clicks: PropTypes.arrayOf(PropTypes.object).isRequired,
    plateMode: PropTypes.string.isRequired,
};

// ─────────────────────────────────────────────────────────────────────────────

const ShotHeatMap = ({ history, hasHeadTarget = true, plateMode = null }) => {
    const [showFirstClicksOnly, setShowFirstClicksOnly] = useState(false);
    const [showPastMonthOnly, setShowPastMonthOnly] = useState(false);

    const clicks = useMemo(() => {
        const allClicks = [];
        const now = new Date();
        const pastMonth = new Date();
        pastMonth.setMonth(now.getMonth() - 1);

        history.forEach(session => {
            if (showPastMonthOnly && session.timestamp && new Date(session.timestamp) < pastMonth) return;

            if (session.clickLocations && Array.isArray(session.clickLocations) && session.clickLocations.length > 0) {
                if (showFirstClicksOnly) {
                    allClicks.push(session.clickLocations[0]);
                } else {
                    allClicks.push(...session.clickLocations);
                }
            }
        });
        return allClicks;
    }, [history, showFirstClicksOnly, showPastMonthOnly]);

    // Target dimensions
    const inchesToView = 12;
    const bodyW = 5.9 * inchesToView;
    const bodyH = 11 * inchesToView;
    const headW = 4 * inchesToView;
    const headH = 6 * inchesToView;
    const headOffsetY = 11 * inchesToView;

    const padX = 200;
    const padTop = 150;
    const padBottom = 150;
    const svgW = bodyW + padX * 2;

    const bodyCY = hasHeadTarget
        ? padTop + (headH / 2) + headOffsetY
        : padTop + (bodyH / 2);

    const svgH = bodyCY + (bodyH / 2) + padBottom;
    const bodyCX = svgW / 2;
    const headCX = bodyCX;
    const headCY = bodyCY - headOffsetY;

    return (
        <div style={{ marginTop: '0', background: '#1a1a2e', padding: '15px', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '15px', width: '100%', maxWidth: '400px' }}>
                <div style={{ display: 'flex', gap: '5px' }}>
                    <button
                        onClick={() => setShowFirstClicksOnly(false)}
                        style={{
                            flex: 1,
                            background: !showFirstClicksOnly ? '#3498db' : '#222',
                            color: !showFirstClicksOnly ? '#fff' : '#888',
                            border: '1px solid #333', padding: '8px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold'
                        }}
                    >
                        All Shots
                    </button>
                    <button
                        onClick={() => setShowFirstClicksOnly(true)}
                        style={{
                            flex: 1,
                            background: showFirstClicksOnly ? '#3498db' : '#222',
                            color: showFirstClicksOnly ? '#fff' : '#888',
                            border: '1px solid #333', padding: '8px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold'
                        }}
                    >
                        First Shot Only
                    </button>
                </div>
                <div style={{ display: 'flex', gap: '5px' }}>
                    <button
                        onClick={() => setShowPastMonthOnly(false)}
                        style={{
                            flex: 1,
                            background: !showPastMonthOnly ? '#9b59b6' : '#222',
                            color: !showPastMonthOnly ? '#fff' : '#888',
                            border: '1px solid #333', padding: '8px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold'
                        }}
                    >
                        All Time
                    </button>
                    <button
                        onClick={() => setShowPastMonthOnly(true)}
                        style={{
                            flex: 1,
                            background: showPastMonthOnly ? '#9b59b6' : '#222',
                            color: showPastMonthOnly ? '#fff' : '#888',
                            border: '1px solid #333', padding: '8px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold'
                        }}
                    >
                        Past Month
                    </button>
                </div>
            </div>

            <p style={{ margin: '0 0 15px 0', color: '#888', fontSize: '12px' }}>
                {clicks.length} {showFirstClicksOnly ? 'first ' : ''}shots recorded {showPastMonthOnly ? 'this month' : 'all time'}
            </p>

            {/* ── Plate mode ─────────────────────────────────────────────── */}
            {plateMode ? (
                <PlateHeatMap clicks={clicks} plateMode={plateMode} />
            ) : (
                <svg width="100%" height="auto" style={{ maxWidth: '600px', background: '#111', borderRadius: '8px', border: '1px solid #333' }} viewBox={`0 0 ${svgW} ${svgH}`}>
                    <defs>
                        <radialGradient id="hitGrad">
                            <stop offset="0%" stopColor="rgba(46, 204, 113, 0.6)" />
                            <stop offset="50%" stopColor="rgba(46, 204, 113, 0.2)" />
                            <stop offset="100%" stopColor="rgba(46, 204, 113, 0)" />
                        </radialGradient>
                        <radialGradient id="missGrad">
                            <stop offset="0%" stopColor="rgba(231, 76, 60, 0.6)" />
                            <stop offset="50%" stopColor="rgba(231, 76, 60, 0.2)" />
                            <stop offset="100%" stopColor="rgba(231, 76, 60, 0)" />
                        </radialGradient>
                    </defs>

                    <g stroke="#222" strokeWidth="1">
                        <line x1={bodyCX} y1="0" x2={bodyCX} y2={svgH} strokeDasharray="5 5" />
                        <line x1="0" y1={bodyCY} x2={svgW} y2={bodyCY} strokeDasharray="5 5" />
                        {hasHeadTarget && <line x1="0" y1={headCY} x2={svgW} y2={headCY} strokeDasharray="5 5" />}
                    </g>

                    <rect x={bodyCX - bodyW / 2} y={bodyCY - bodyH / 2} width={bodyW} height={bodyH} fill="#3f3323" stroke="#8B4513" strokeWidth="2" />
                    <rect x={bodyCX - bodyW / 2 + 4} y={bodyCY - bodyH / 2 + 4} width={bodyW - 8} height={bodyH - 8} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeDasharray="4 4" />

                    {hasHeadTarget && (
                        <>
                            <rect x={headCX - headW / 2} y={headCY - headH / 2} width={headW} height={headH} fill="#3f3323" stroke="#8B4513" strokeWidth="2" />
                            <rect x={headCX - headW / 2 + 4} y={headCY - headH / 2 + 4} width={headW - 8} height={headH - 8} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeDasharray="4 4" />
                        </>
                    )}

                    {clicks.map((c, i) => {
                        let cx, cy;
                        if (c.zone === 'body') {
                            cx = bodyCX + c.x * (bodyW / 2);
                            cy = bodyCY + c.y * (bodyH / 2);
                        } else if (c.zone === 'head') {
                            if (!hasHeadTarget) return null;
                            cx = headCX + c.x * (headW / 2);
                            cy = headCY + c.y * (headH / 2);
                        } else {
                            return null;
                        }

                        cx = Math.max(0, Math.min(svgW, cx));
                        cy = Math.max(0, Math.min(svgH, cy));

                        return (
                            <circle key={i} cx={cx} cy={cy} r="16" fill={c.hit ? 'url(#hitGrad)' : 'url(#missGrad)'} style={{ mixBlendMode: 'screen' }} />
                        );
                    })}
                </svg>
            )}

            <div style={{ display: 'flex', gap: '20px', marginTop: '15px', fontSize: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '12px', height: '12px', background: 'rgba(46, 204, 113, 0.6)', borderRadius: '50%' }}></div>
                    <span style={{ color: '#ccc' }}>Hits</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '12px', height: '12px', background: 'rgba(231, 76, 60, 0.6)', borderRadius: '50%' }}></div>
                    <span style={{ color: '#ccc' }}>Misses</span>
                </div>
            </div>
        </div>
    );
};

ShotHeatMap.propTypes = {
    history: PropTypes.arrayOf(PropTypes.object).isRequired,
    hasHeadTarget: PropTypes.bool,
    plateMode: PropTypes.string,
};

export default ShotHeatMap;
